import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core'
import { MbCheckoutCommonService } from '../../services/mb-checkout-common.service'
import {
  Address,
  Country,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  isNotNullable,
  TranslationService,
  UserPaymentService,
  WindowRef
} from '@spartacus/core'
import { ActivatedRoute } from '@angular/router'
import { EMPTY, Observable, Subscription } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MbConstants } from '../../../shared/mb-constants'
import { MbCheckoutBillingAddressesConnector, MbPaymentModeConnector } from '../../connectors'
import { MbCheckoutStepService } from '../../services/mb-checkout-step.service'
import { MbCheckoutBillingFacade } from '../../facades/mb-checkout-billing.facade'
import {
  MbBillingInformation,
  MbCheckoutTypes,
  MbPaymentMode,
  MbPaymentModeSelection
} from '../../../models'
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root'
import { CheckoutPaymentMethodComponent, CheckoutStepService } from '@spartacus/checkout/base/components'
import {
  CheckoutDeliveryAddressFacade,
  CheckoutPaymentFacade
} from '@spartacus/checkout/base/root'
import { CheckoutBillingAddressSetEvent } from '../../../analytics/events/mb-checkout.events'

@Component({
  selector: 'mb-checkout-billing-information-base',
  template: '',
})
export class MbCheckoutBillingInformationBaseComponent extends CheckoutPaymentMethodComponent implements OnInit, OnDestroy {

  isCardViewVisible = true
  country: Country | undefined
  selectedAddress: Address | undefined
  paymentModes$: Observable<MbPaymentMode[]> | undefined
  selectedPaymentModeSelection: MbPaymentModeSelection | undefined
  billingInformation: MbBillingInformation = {}
  billingInformationForm: UntypedFormGroup = this.convertCheckoutBillingInformationModel2Form()
  constants = MbConstants
  shouldLeavePage = false
  subscription = new Subscription()
  isPurchaseCheckoutType = false
  checkoutTypes = MbCheckoutTypes
  addresses$ = this.activeCartService.getActive().pipe(
    filter(isNotNullable),
    switchMap(cart => {
      if (this.shouldLeavePage) {
        this.shouldLeavePage = false
        this.mbCheckoutStepService.next(this.activatedRoute)
      }
      else {
        if (cart?.paymentDetails?.billingAddress?.verificationStatus !== this.constants.ADDRESS_VERIFIED_REJECTED_STATUS_04) {
          this.selectedAddress = cart?.paymentDetails?.billingAddress
        }
        const checkoutType = cart.checkoutStatus?.checkoutType
        this.isPurchaseCheckoutType =
          checkoutType === this.checkoutTypes.CHECKOUT_TYPE_D || checkoutType === this.checkoutTypes.CHECKOUT_TYPE_E || false
        if (this.isPurchaseCheckoutType) {
          this.setPaymentModeFromCart(cart)
        } else {
          const paymentMode: MbPaymentModeSelection = { paymentMode: { code: this.constants.PLACEHOLDER_PAYMENT_MODE_CODE } }
          this.paymentModeSelectedHandler(paymentMode)
        }
        this.billingInformationForm = this.convertCheckoutBillingInformationModel2Form()
        if (!!cart?.code) {
          //TODO this needs some serious refactoring
          this.paymentModes$ = this.isPurchaseCheckoutType && this.mbPaymentModeConnector.get(cart?.code).pipe(
            filter(isNotNullable),
            map(paymentModes => paymentModes?.sort(paymentMode => paymentMode?.poPaymentMode ? -1 : 1)),
            tap(paymentModes => {
              const defaultPaymentModeSelection: MbPaymentModeSelection = {
                paymentMode: paymentModes?.find((paymentMode: MbPaymentMode) =>
                  paymentMode?.code === this.constants.INVOICE_CODE) || paymentModes[0]
              }
              if (!this.selectedPaymentModeSelection?.paymentMode) {
                this.selectedPaymentModeSelection = {
                  paymentMode: paymentModes?.find((paymentMode: MbPaymentMode) => paymentMode?.preselected)
                } as MbPaymentModeSelection
                if (!this.selectedPaymentModeSelection?.paymentMode) {
                  this.paymentModeSelectedHandler(defaultPaymentModeSelection)
                }
              } else {
                const isSelectedPaymentModeDisplayed: boolean = paymentModes?.findIndex((paymentMode: MbPaymentMode) =>
                  paymentMode?.code === this.selectedPaymentModeSelection?.paymentMode?.code) > -1
                if (!isSelectedPaymentModeDisplayed) {
                  this.paymentModeSelectedHandler(defaultPaymentModeSelection)
                }
              }
              this.billingInformationForm = this.convertCheckoutBillingInformationModel2Form()
            })
          ) || EMPTY
        }
      }
      return this.mbCheckoutBillingAddressesConnector.get()
    }),
    tap((addresses: any[]) => {
      if (!this.selectedAddress) {
        this.selectedAddress = addresses.find((address: any) =>
          address?.defaultBillingAddress && address?.verificationStatus !== this.constants.ADDRESS_VERIFIED_REJECTED_STATUS_04)
        this.billingInformationForm = this.convertCheckoutBillingInformationModel2Form()
      }
    }))

  constructor (protected userPaymentService: UserPaymentService,
               protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
               protected checkoutPaymentService: CheckoutPaymentFacade,
               protected globalMessageService: GlobalMessageService,
               protected activatedRoute: ActivatedRoute,
               protected translation: TranslationService,
               protected activeCartService: ActiveCartFacade,
               protected checkoutStepService: CheckoutStepService,
               protected mbCheckoutCommonService: MbCheckoutCommonService,
               protected mbCheckoutBillingAddressesConnector: MbCheckoutBillingAddressesConnector,
               protected mbPaymentModeConnector: MbPaymentModeConnector,
               protected mbCheckoutStepService: MbCheckoutStepService,
               protected mbCheckoutBillingFacade: MbCheckoutBillingFacade,
               protected el: ElementRef,
               protected winRef: WindowRef,
               protected eventService: EventService) {
    super(userPaymentService, checkoutDeliveryAddressFacade, checkoutPaymentService, activatedRoute, translation,
      activeCartService, checkoutStepService, globalMessageService)
  }

  ngOnInit () {
    this.subscription?.add(this.mbCheckoutBillingFacade.isCardViewAndPaymentVisible
      .subscribe(showCardView => this.isCardViewVisible = showCardView))
    this.subscription?.add(this.mbCheckoutCommonService.userCountry
      .subscribe(country => this.country = country))
    this.subscription?.add(this.eventService.get(CheckoutBillingAddressSetEvent).subscribe(payload => {
      this.shouldLeavePage = !!payload?.isSuccessful
      if (!payload?.isSuccessful) {
        console.error(payload?.error)
        this.globalMessageService.add({ key: 'address.error' }, GlobalMessageType.MSG_TYPE_ERROR)
      }
      })
    )
  }

  ngOnDestroy () {
    this.subscription?.add(this.activeCartService.isGuestCart().pipe(
      tap(isGuestCart => !isGuestCart ? this.mbCheckoutBillingFacade.openCardView() : undefined)
    ).subscribe())
    this.subscription?.unsubscribe()
  }

  next () {
    if (!this.billingInformationForm?.valid || !this.isPoNumberValid()) {
      this.mbCheckoutBillingFacade.notifyPoNumberFieldValidity(true)
      this.billingInformationForm?.markAllAsTouched()
      this.mbCheckoutCommonService.scrollToError(this.billingInformationForm, this.el, this.winRef)
      this.globalMessageService.add({ key: 'address.mandatoryMissingError' }, GlobalMessageType.MSG_TYPE_ERROR)
    } else {
      this.mbCheckoutBillingFacade.notifyPoNumberFieldValidity(false)
      this.postForm()
    }
  }

  back () {
    this.mbCheckoutStepService.back(this.activatedRoute)
  }

  addressSelectedHandler (event: Address) {
    this.selectedAddress = event
    this.billingInformationForm?.patchValue({ billingAddress: event })
    if (!this.isPoNumberValid()) {
      this.billingInformationForm?.setErrors({ required: true })
    }
  }

  addNewAddressHandler () {
    if (this.isPoNumberValid()) {
      this.mbCheckoutBillingFacade.setSelectedPaymentMode(this.selectedPaymentModeSelection)
      this.mbCheckoutBillingFacade.closeCardView()
      this.mbCheckoutBillingFacade.notifyPoNumberFieldValidity(false)
    } else {
      this.mbCheckoutBillingFacade.notifyPoNumberFieldValidity(true)
      this.globalMessageService.add({ key: 'address.mandatoryMissingError' }, GlobalMessageType.MSG_TYPE_ERROR)
    }
  }

  setPaymentModeFromCart (cart: Cart) {
    if (this.isPurchaseCheckoutType && cart?.paymentDetails?.paymentMode) {
      this.selectedPaymentModeSelection = {
        paymentMode: cart?.paymentDetails?.paymentMode,
        poNumber: cart?.paymentDetails?.poNumber
      }
    }
  }

  paymentModeSelectedHandler (event: MbPaymentModeSelection) {
    this.selectedPaymentModeSelection = event
    this.billingInformationForm?.patchValue({ paymentModeSelection: event })
    if (!this.isPoNumberValid()) {
      this.billingInformationForm?.setErrors({ required: true })
    }
  }

  convertCheckoutBillingInformationModel2Form () {
    return new UntypedFormGroup({
      billingAddress: new UntypedFormControl(this.selectedAddress, Validators.required),
      paymentModeSelection: new UntypedFormControl(this.selectedPaymentModeSelection, Validators.required)
    })
  }

  convertCheckoutBillingInformationForm2Model () {
    this.billingInformation = {
      ...this.billingInformation,
      billingAddress: this.billingInformationForm?.get(this.constants.BILLING_ADDRESS_CODE)?.value,
      paymentModeSelection: this.billingInformationForm?.get(this.constants.PAYMENT_MODE_SELECTION)?.value
    }
  }

  postForm () {
    this.convertCheckoutBillingInformationForm2Model()
    this.mbCheckoutBillingFacade.postBillingStepForm(this.billingInformation)
  }

  isPoNumberValid () {
    return !this.isPurchaseCheckoutType || !!this.selectedPaymentModeSelection?.poNumber
  }
}
